import { isPgaLink } from 'lib/utils'
import { DateTime } from 'luxon'
import { PGA_HOPE_GROUP_EVENT_TAG } from 'lib/group-events/adm-promotion-tip'

export const MARKETPLACE_LISTING_REASONS = {
  ADM_INCOMPLETE: 'adm-incomplete',
  PROFILE_HIDDEN: 'profile-hidden',
  NO_VISIBLE_FACILITY: 'no-visible-facility',
  LEAD_LIMIT_REACHED: 'lead-limit-reached',
  LEAD_COLLECTION_DISABLED: 'lead-collection-disabled',
  NOT_BOOKABLE: 'not-bookable',
  BOOKING_TYPES_PRIVATE: 'booking-types-private',
}

export const isExternalBookingUrl = url => {
  if (!url) return false

  try {
    return !isPgaLink(url)
  } catch {
    return false
  }
}

export const hasVisibleFacility = (facilities = []) =>
  (facilities ?? []).some(facility =>
    facility.coachProfilesFacilities?.some(cpf => cpf.visible),
  )

export const hasReachedLeadLimit = coach =>
  (coach?.freeTierLimitsReached ?? []).includes('LEADS')

export const canReceiveLeads = coach =>
  !hasReachedLeadLimit(coach) && Boolean(coach?.preferences?.acceptsLeads)

// Private booking types have status INACTIVE and can only be booked by the
// coach.
export const hasOnlyPrivateBookingTypes = (lessonTypes = []) =>
  (lessonTypes ?? []).length > 0 &&
  lessonTypes.every(lessonType => lessonType.status !== 'ACTIVE')

// `bookable` may count private booking types, so a coach whose booking types
// are all private is treated as unbookable unless they use an outside tool.
export const canBeBooked = (coach, { lessonTypes } = {}) =>
  isExternalBookingUrl(coach?.bookingUrl) ||
  (Boolean(coach?.coachProfile?.bookable) &&
    !hasOnlyPrivateBookingTypes(lessonTypes))

// Coaches without a profile get no reasons; creating one is its own onboarding
// step.
export const isMarketplaceCandidate = coach => Boolean(coach?.coachProfile)

// The groupEvents startDateTime filter may match on an event's first session,
// so look back far enough to keep multi-week programs that are already
// underway; hasUpcomingSession decides what is still upcoming.
export const getPgaHopeGroupEventsStartDateTime = (now = DateTime.local()) => ({
  from: now.minus({ months: 6 }).toISO(),
  to: null,
})

const hasUpcomingSession = (event, now = DateTime.local()) => {
  const sessions = event?.sessions?.length
    ? event.sessions
    : [{ startDateTime: event?.startDateTime }]

  return sessions.some(session => {
    const startDateTime = DateTime.fromISO(session?.startDateTime ?? '')
    return startDateTime.isValid && startDateTime > now
  })
}

export const hasUpcomingPgaHopeRegistrationOpportunity = (
  groupEvents = [],
  now = DateTime.local(),
) =>
  (groupEvents ?? []).some(event => {
    // registrationClosed is also true before registration opens, so events
    // that are not open yet do not count.
    const isRegistrationOpen = event.registrationClosed === false
    const isCollectingRegistrations =
      isRegistrationOpen && !event.registrationFull
    const hasOpenWaitlist =
      isRegistrationOpen && event.registrationFull && event.hasWaitlist

    return (
      event?.tags?.includes(PGA_HOPE_GROUP_EVENT_TAG) &&
      hasUpcomingSession(event, now) &&
      (isCollectingRegistrations || hasOpenWaitlist)
    )
  })

// Visibility can't be turned on until ADM is complete, so the hidden profile
// reason only shows once ADM is done.
export const getMarketplaceListingReasons = (coach, { lessonTypes } = {}) => {
  if (!isMarketplaceCandidate(coach)) {
    return []
  }

  const reasons = []

  if (!coach.canPromoteContent) {
    reasons.push(MARKETPLACE_LISTING_REASONS.ADM_INCOMPLETE)
  } else if (coach.coachProfile.status !== 'ACTIVE') {
    reasons.push(MARKETPLACE_LISTING_REASONS.PROFILE_HIDDEN)
  }

  if (!hasVisibleFacility(coach.facilities)) {
    reasons.push(MARKETPLACE_LISTING_REASONS.NO_VISIBLE_FACILITY)
  }

  if (!canReceiveLeads(coach) && !canBeBooked(coach, { lessonTypes })) {
    reasons.push(
      hasReachedLeadLimit(coach)
        ? MARKETPLACE_LISTING_REASONS.LEAD_LIMIT_REACHED
        : MARKETPLACE_LISTING_REASONS.LEAD_COLLECTION_DISABLED,
    )
    reasons.push(
      hasOnlyPrivateBookingTypes(lessonTypes)
        ? MARKETPLACE_LISTING_REASONS.BOOKING_TYPES_PRIVATE
        : MARKETPLACE_LISTING_REASONS.NOT_BOOKABLE,
    )
  }

  return reasons
}
